import React, { useState, useEffect } from 'react';
import Box from 'Components/Atoms/Box';
import Typography from 'Components/Atoms/Typography';
import Button from 'Components/Atoms/Button';
import IconButton from 'Components/Atoms/IconButton';
import {
  Close,
  Edit,
  Delete,
  PlayArrow,
  Pause,
  Event,
} from '@material-ui/icons';
import { RegularTable } from 'types/reservations';
import { Reservation } from 'types/reservations';
import useCollection from 'CustomHooks/useCollection';
import usePinAuth from 'CustomHooks/usePinAuth';
import { blockToTime, toEuropeanDate } from 'utils/helper';
import { Link } from 'react-router-dom';
import { db } from 'utils/firebase';
import './RegularTableDetailSidebar.scss';

export type RegularTableDetailSidebarProps = {
  regularTable: RegularTable;
  restaurantId: string;
  onClose: () => void;
  onEdit: (table: RegularTable) => void;
};

const formatRepeatingPattern = (rule: any): string => {
  if (!rule) return 'Keine Wiederholung';

  switch (rule.type) {
    case 'weekly': {
      const dayNames = [
        'Sonntag',
        'Montag',
        'Dienstag',
        'Mittwoch',
        'Donnerstag',
        'Freitag',
        'Samstag',
      ];
      const days = (rule.weekDays || rule.days || [])
        .map((d: number) => dayNames[d])
        .join(', ');
      return days ? `Wöchentlich: ${days}` : 'Wöchentlich';
    }
    case 'monthlySpecificDay':
    case 'monthly_fix_day': {
      return `Monatlich: Am ${rule.day || 1}. Tag des Monats`;
    }
    case 'monthlyDayOfWeek':
    case 'monthly': {
      const week = rule.option ?? rule.week;
      const weekStr = week === -1 ? 'Letzter' : `${week || 1}.`;
      const dayNames = [
        'Sonntag',
        'Montag',
        'Dienstag',
        'Mittwoch',
        'Donnerstag',
        'Freitag',
        'Samstag',
      ];
      const dayIndex = rule.weekDay ?? (rule.days?.[0] || 0);
      return `Monatlich: ${weekStr} ${dayNames[dayIndex]} des Monats`;
    }
    case 'yearlySpecificDate':
    case 'yearly': {
      return `Jährlich: Am ${rule.date || '01.01'}`;
    }
    default:
      return 'Unbekannt';
  }
};

const RegularTableDetailSidebar: React.FC<RegularTableDetailSidebarProps> = ({
  regularTable,
  restaurantId,
  onClose,
  onEdit,
}) => {
  const [relatedReservations, setRelatedReservations] =
    useCollection<Reservation>('reservations', {
      filter: [
        ['restaurant', '==', restaurantId],
        ['regularTableId', '==', regularTable.id],
      ],
      sort: ['date', 'desc'],
      limit: 100,
      keepDataWhileLoading: true,
    });

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const { authenticate } = usePinAuth();

  // Support both new (top-level) and old (nested reservation) field formats
  const rt = regularTable as any;
  const guest = regularTable.guest || rt.reservation?.guest;
  const time = regularTable.time || rt.reservation?.time;
  const guests = regularTable.guests || rt.reservation?.guests || 2;
  const tables = regularTable.tables || rt.reservation?.tables;
  const reservationLength =
    regularTable.reservationLength ||
    (rt.reservation?.reservationLength
      ? rt.reservation.reservationLength * 15
      : 0);
  const space = regularTable.space || rt.reservation?.space;
  const occassion = regularTable.occassion || rt.reservation?.occassion;
  const repeatingRule = regularTable.repeating || rt.rule;
  const startDate = regularTable.firstReservationAt || rt.reservation?.date;
  const endDate = regularTable.lastReservation || rt.endDate;

  const handleToggleActive = async () => {
    try {
      await db.collection('regularTables').doc(regularTable.id).update({
        active: !regularTable.active,
        updatedAt: Date.now(),
      });
      console.log('Toggled active status');
    } catch (error) {
      console.error('Error toggling active status:', error);
      alert(
        'Fehler beim Aktualisieren des Status: ' + (error as Error).message
      );
    }
  };

  const handleDelete = async () => {
    try {
      await db.collection('regularTables').doc(regularTable.id).delete();
      console.log('Deleted regular table');
      setShowDeleteConfirm(false);
      onClose();
    } catch (error) {
      console.error('Error deleting regular table:', error);
      alert('Fehler beim Löschen: ' + (error as Error).message);
      setShowDeleteConfirm(false);
    }
  };

  return (
    <>
      <Box className="regular-table-detail-sidebar-overlay" onClick={onClose} />
      <Box className="regular-table-detail-sidebar">
        <Box className="sidebar-header">
          <Typography variant="h3">Stammtisch Details</Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>

        <Box className="sidebar-content">
          {/* Status Section */}
          <Box className="detail-section">
            <Typography
              variant="text-5"
              weight="bold"
              className="section-label"
            >
              STATUS
            </Typography>
            <Box flex style={{ alignItems: 'center', gap: '0.5rem' }}>
              <Box
                className={`status-badge ${
                  regularTable.active ? 'active' : 'inactive'
                }`}
              >
                {regularTable.active ? 'Aktiv' : 'Inaktiv'}
              </Box>
              <Button
                endIcon={(p) =>
                  regularTable.active ? <Pause {...p} /> : <PlayArrow {...p} />
                }
                onClick={() =>
                  regularTable.active
                    ? authenticate(
                        'reservations.cancelReservation.write',
                        handleToggleActive
                      )
                    : handleToggleActive()
                }
              >
                {regularTable.active ? 'Deaktivieren' : 'Aktivieren'}
              </Button>
            </Box>
          </Box>

          {/* Guest Information */}
          <Box className="detail-section">
            <Typography
              variant="text-5"
              weight="bold"
              className="section-label"
            >
              GAST INFORMATIONEN
            </Typography>
            <Box>
              <Typography
                variant="text-2"
                weight="medium"
                style={{ marginBottom: '0.75rem', display: 'block' }}
              >
                {guest?.name || 'Kein Name'}
              </Typography>
              {guest?.email && (
                <Typography
                  variant="text-3"
                  color="subdued"
                  style={{ marginBottom: '0.5rem', display: 'block' }}
                >
                  {guest.email}
                </Typography>
              )}
              {guest?.phone && (
                <Typography
                  variant="text-3"
                  color="subdued"
                  style={{ marginBottom: '0.5rem', display: 'block' }}
                >
                  {guest.phone}
                </Typography>
              )}
              <Typography
                variant="text-3"
                style={{ marginTop: '0.75rem', display: 'block' }}
              >
                <strong>Anzahl Gäste:</strong> {guests}
              </Typography>
            </Box>
          </Box>

          {/* Reservation Details */}
          <Box className="detail-section">
            <Typography
              variant="text-5"
              weight="bold"
              className="section-label"
            >
              RESERVIERUNG DETAILS
            </Typography>
            <Box>
              <Typography
                variant="text-3"
                style={{ marginBottom: '0.5rem', display: 'block' }}
              >
                <strong>Zeit:</strong> {time || 'N/A'} Uhr
              </Typography>
              <Typography
                variant="text-3"
                style={{ marginBottom: '0.5rem', display: 'block' }}
              >
                <strong>Dauer:</strong>{' '}
                {blockToTime(Math.ceil(reservationLength / 15))} Std.
              </Typography>
              <Typography
                variant="text-3"
                style={{ marginBottom: '0.5rem', display: 'block' }}
              >
                <strong>Tische:</strong>{' '}
                {tables && tables.length > 0
                  ? tables.join(', ')
                  : 'Keine Tische'}
              </Typography>
              {space && (
                <Typography
                  variant="text-3"
                  style={{ marginBottom: '0.5rem', display: 'block' }}
                >
                  <strong>Raum:</strong> {space}
                </Typography>
              )}
              {occassion && (
                <Typography
                  variant="text-3"
                  style={{ marginBottom: '0.5rem', display: 'block' }}
                >
                  <strong>Anlass:</strong> {occassion}
                </Typography>
              )}
              <Typography variant="text-3" style={{ display: 'block' }}>
                <strong>E-Mail senden:</strong>{' '}
                {regularTable.sendEmail ? 'Ja' : 'Nein'}
              </Typography>
            </Box>
          </Box>

          {/* Repeating Pattern */}
          <Box className="detail-section">
            <Typography
              variant="text-5"
              weight="bold"
              className="section-label"
            >
              WIEDERHOLUNG
            </Typography>
            <Typography
              variant="text-3"
              style={{ marginBottom: '0.5rem', display: 'block' }}
            >
              {formatRepeatingPattern(repeatingRule)}
            </Typography>
            <Typography
              variant="text-3"
              style={{ marginBottom: '0.5rem', display: 'block' }}
            >
              <strong>Start:</strong>{' '}
              {startDate ? toEuropeanDate(startDate) : 'N/A'}
            </Typography>
            {endDate && (
              <Typography
                variant="text-3"
                style={{ marginBottom: '0.5rem', display: 'block' }}
              >
                <strong>Ende:</strong> {toEuropeanDate(endDate)}
              </Typography>
            )}
            <Typography variant="text-3" style={{ display: 'block' }}>
              <strong>Tage im Voraus:</strong>{' '}
              {regularTable.xDaysInFuture || 180} Tage
            </Typography>
          </Box>

          {/* Notes */}
          {regularTable.notes && (
            <Box className="detail-section">
              <Typography
                variant="text-5"
                weight="bold"
                className="section-label"
              >
                NOTIZEN
              </Typography>
              <Typography variant="text-3" style={{ whiteSpace: 'pre-wrap' }}>
                {regularTable.notes}
              </Typography>
            </Box>
          )}

          {/* Related Reservations */}
          <Box className="detail-section">
            <Typography
              variant="text-5"
              weight="bold"
              className="section-label"
            >
              VERKNÜPFTE RESERVIERUNGEN ({relatedReservations.data.length})
            </Typography>
            {relatedReservations.loading ? (
              <Typography variant="text-3">Lade Reservierungen...</Typography>
            ) : relatedReservations.data.length === 0 ? (
              <Typography variant="text-3" color="subdued">
                Keine Reservierungen vorhanden
              </Typography>
            ) : (
              <Box className="reservations-list">
                {relatedReservations.data.slice(0, 10).map((reservation) => (
                  <Link
                    key={reservation.id}
                    to={`/${restaurantId}/reservations/${reservation.date}/tablePlan`}
                    className="reservation-item"
                    onClick={onClose}
                  >
                    <Box flex style={{ alignItems: 'center', gap: '0.5rem' }}>
                      <Event fontSize="small" style={{ color: '#666' }} />
                      <Box>
                        <Typography variant="text-3" weight="medium">
                          {toEuropeanDate(reservation.date)} -{' '}
                          {reservation.time}
                        </Typography>
                        <Typography variant="text-4" color="subdued">
                          2 Gäste •{' '}
                          {/* TODO: Fix - {reservation.guest?.guests || 0} */}
                          {reservation.tables?.join(', ')}
                        </Typography>
                      </Box>
                    </Box>
                  </Link>
                ))}
                {relatedReservations.data.length > 10 && (
                  <Typography
                    variant="text-4"
                    style={{ padding: '0.5rem', color: '#666' }}
                  >
                    ... und {relatedReservations.data.length - 10} weitere
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </Box>

        <Box className="sidebar-footer">
          <Button
            variant="outlined"
            endIcon={(p) => <Edit {...p} />}
            onClick={() => onEdit(regularTable)}
            style={{ flex: 1 }}
          >
            Bearbeiten
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            endIcon={(p) => <Delete {...p} />}
            onClick={() => setShowDeleteConfirm(true)}
            style={{ flex: 1 }}
          >
            Löschen
          </Button>
        </Box>

        {/* Delete Confirmation */}
        {showDeleteConfirm && (
          <Box className="delete-confirm-overlay">
            <Box className="delete-confirm-modal">
              <Typography variant="h3" style={{ marginBottom: '1rem' }}>
                Stammtisch löschen?
              </Typography>
              <Typography
                variant="text-2"
                style={{ marginBottom: '2rem', color: '#666' }}
              >
                Möchten Sie diesen Stammtisch wirklich löschen? Diese Aktion
                kann nicht rückgängig gemacht werden. Die verknüpften
                Reservierungen bleiben erhalten.
              </Typography>
              <Box flex style={{ gap: '1rem', justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={() => setShowDeleteConfirm(false)}
                >
                  Abbrechen
                </Button>
                <Button
                  color="secondary"
                  onClick={() =>
                    authenticate(
                      'reservations.cancelReservation.write',
                      handleDelete
                    )
                  }
                >
                  Löschen
                </Button>
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </>
  );
};

export default RegularTableDetailSidebar;
